import { useEffect, useState } from "react";
import { Eye, FileText, Pencil, Plus, Save, Trash2, Users } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  createTemplate,
  deleteTemplate,
  listTemplates,
  updateTemplate,
} from "@/api/documents";
import type { DocumentTemplate } from "@/types/document";
import { DOC_TYPE_LABELS } from "@/types/document";
import { BulkAssignModal } from "@/components/documents/BulkAssignModal";
import { TemplatePreviewModal } from "@/components/documents/TemplatePreviewModal";

interface TemplateForm {
  name: string;
  type: string;
  description: string;
  content_template: string;
  default_requires_signature: boolean;
  default_expires_days: string;
  is_active: boolean;
}

const EMPTY: TemplateForm = {
  name: "",
  type: "offer_letter",
  description: "",
  content_template:
    "Dear {{employee.first_name}},\n\nWelcome to the team.\n\nSigned,\n{{today}}",
  default_requires_signature: false,
  default_expires_days: "",
  is_active: true,
};

export function DocumentTemplatesSettings() {
  const { addToast } = useToast();
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [open, setOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTemplate, setAssignTemplate] = useState<DocumentTemplate | null>(
    null,
  );
  const [editing, setEditing] = useState<DocumentTemplate | null>(null);
  const [form, setForm] = useState<TemplateForm>(EMPTY);
  // Preview modal state — separate from edit so HR can scan the
  // document without committing to the edit form. ``previewing``
  // doubles as the "is open" flag (null === closed) which keeps the
  // open/close lifecycle simple.
  const [previewing, setPreviewing] = useState<DocumentTemplate | null>(null);

  const load = async () => {
    try {
      const data = await listTemplates(true);
      setTemplates(data);
    } catch {
      addToast("error", "Failed to load templates");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (t: DocumentTemplate) => {
    setEditing(t);
    setForm({
      name: t.name,
      type: t.type,
      description: t.description ?? "",
      content_template: t.content_template ?? "",
      default_requires_signature: t.default_requires_signature,
      default_expires_days:
        t.default_expires_days != null ? String(t.default_expires_days) : "",
      is_active: t.is_active,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload: Partial<DocumentTemplate> = {
        name: form.name.trim(),
        type: form.type.trim(),
        description: form.description || null,
        content_template: form.content_template || null,
        default_requires_signature: form.default_requires_signature,
        default_expires_days: form.default_expires_days
          ? Number(form.default_expires_days)
          : null,
        is_active: form.is_active,
      };
      if (editing) {
        await updateTemplate(editing.id, payload);
      } else {
        await createTemplate(payload);
      }
      setOpen(false);
      addToast("success", "Template saved");
      await load();
    } catch {
      addToast("error", "Failed to save");
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteTemplate(id);
      addToast("success", "Template deactivated");
      await load();
    } catch {
      addToast("error", "Failed to delete");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Document templates</CardTitle>
          <Button size="sm" onClick={openNew}>
            <Plus className="h-4 w-4" />
            New template
          </Button>
        </CardHeader>

        {templates.length === 0 ? (
          <div className="py-8 text-center text-sm text-text-muted">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No templates yet. Create one so you can bulk-assign documents (NDAs,
            policies, offer letters, etc.).
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {templates.map((t) => (
              <div
                key={t.id}
                className="py-3 flex items-center gap-3 justify-between"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-text-primary truncate">
                      {t.name}
                    </p>
                    <Badge variant="default">
                      {DOC_TYPE_LABELS[t.type] ?? t.type}
                    </Badge>
                    {t.default_requires_signature && (
                      <Badge variant="info">Signature</Badge>
                    )}
                    {!t.is_active && <Badge variant="default">Inactive</Badge>}
                  </div>
                  {t.description && (
                    <p className="text-xs text-text-muted mt-0.5 truncate">
                      {t.description}
                    </p>
                  )}
                </div>
                {/* Preview goes first — it's the safest action (read-only)
                    and the most common one HR will want when scanning the
                    list. Order: Preview | Assign | Edit | Delete. */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewing(t)}
                  title="Preview"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setAssignTemplate(t);
                    setAssignOpen(true);
                  }}
                  title="Bulk assign"
                  disabled={!t.is_active}
                >
                  <Users className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(t)}
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                {t.is_active && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(t.id)}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4 text-danger" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit template" : "New template"}
        size="lg"
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Standard NDA"
            />
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">
                Type
              </label>
              <Select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                options={Object.entries(DOC_TYPE_LABELS).map(([v, l]) => ({
                  value: v,
                  label: l,
                }))}
              />
            </div>
          </div>
          <Input
            label="Description"
            value={form.description}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
          />
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">
              Content (Markdown, use {`{{employee.first_name}}`} etc.)
            </label>
            <textarea
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm font-mono text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
              rows={10}
              value={form.content_template}
              onChange={(e) =>
                setForm({ ...form, content_template: e.target.value })
              }
            />
            <p className="text-[11px] text-text-muted mt-1">
              Placeholders: {" "}
              <code>{"{{employee.first_name}}"}</code>,{" "}
              <code>{"{{employee.last_name}}"}</code>,{" "}
              <code>{"{{employee.full_name}}"}</code>,{" "}
              <code>{"{{employee.email}}"}</code>,{" "}
              <code>{"{{employee.title}}"}</code>,{" "}
              <code>{"{{employee.hire_date}}"}</code>, {" "}
              <code>{"{{today}}"}</code>, <code>{"{{year}}"}</code>
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Default expires in (days)"
              type="number"
              value={form.default_expires_days}
              onChange={(e) =>
                setForm({ ...form, default_expires_days: e.target.value })
              }
            />
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="rounded border-border text-accent"
                  checked={form.default_requires_signature}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      default_requires_signature: e.target.checked,
                    })
                  }
                />
                Require signature by default
              </label>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-border text-accent"
              checked={form.is_active}
              onChange={(e) =>
                setForm({ ...form, is_active: e.target.checked })
              }
            />
            Active
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save}>
              <Save className="h-4 w-4" />
              Save
            </Button>
          </div>
        </div>
      </Modal>

      {assignTemplate && (
        <BulkAssignModal
          isOpen={assignOpen}
          onClose={() => {
            setAssignOpen(false);
            setAssignTemplate(null);
          }}
          template={assignTemplate}
        />
      )}

      {/* Preview modal — single instance reused for whichever row was
          last clicked. Closing returns ``previewing`` to null which
          unmounts the iframe so the PDF stops streaming. */}
      <TemplatePreviewModal
        template={previewing}
        isOpen={previewing !== null}
        onClose={() => setPreviewing(null)}
        onEdit={(t) => {
          setPreviewing(null);
          openEdit(t);
        }}
      />
    </div>
  );
}
